import { ChangeEvent, useEffect, useState } from 'react'
import { OnFieldChangePayload } from '../@types/Input.types'
import { useInView } from 'react-intersection-observer'
import { HttpClient } from './utils'
import ComicCardType from '../@types/Comics.types'
import ApiUtils from './api.utils'
import { toast } from 'react-toastify'
import axios from 'axios'
import { loadIntercom } from 'next-intercom'
import { UserProfile } from '../@types/User.types'

export function useTextField(defaultValue?: string) {
  const [value, setValue] = useState(defaultValue || '')

  function handleChange(payload: OnFieldChangePayload) {
    setValue(payload.value)
  }
  return {
    value,
    onChange: handleChange,
  }
}

type useInfiniteLoaderParams = {
  data: any[]
  meta?: {
    pages: number
    total: number
    next: number | null
  }
  path: string
}

export function useInfiniteLoader(params: useInfiniteLoaderParams) {
  const [fetchError, setFetchError] = useState('')
  const [loaderVisibility, setLoaderVisibility] = useState(true)
  const [fetching, setFetching] = useState(false)
  const [data, setData] = useState(params.data)
  const [meta, setMeta] = useState(params.meta)
  const { ref, inView } = useInView({
    threshold: 1,
  })

  async function fetchData() {
    if (!meta?.next) return
    setFetching(true)
    const { result, error } = await HttpClient({
      path: params.path,
      params: {
        p: meta.next,
      },
    })
    setFetching(false)

    if (error) {
      setLoaderVisibility(false)
      setFetchError(error.message)
      return
    }

    if (result) {
      setMeta(result.data.meta)
      setData((__d) => __d.concat(result.data.comics || result.data.comments))
    }
  }

  useEffect(() => {
    if (meta && !meta.next) {
      setLoaderVisibility(false)
    }
  }, [meta])

  useEffect(() => {
    if (!fetching && inView) {
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView])

  return {
    ref,
    fetchError,
    fetching,
    fetchData,
    loaderVisibility,
    data,
    meta,
  }
}

export function useBookmarkComic(comic: ComicCardType) {
  const [loading, setLoading] = useState(false)
  // const [error, setError] = useState('')
  const [bookmarked, setBookmarked] = useState(comic.bookmarked)

  async function handleBookmark() {
    setLoading(true)
    const { error } = await HttpClient({
      path:
        ApiUtils.Endpoints.activities.bookmark_single.mirror + `/${comic.id}`,
    })
    setLoading(false)

    if (error) {
      toast.error('Failed to bookmark comic, please try again')
      return
    }

    if (!error) {
      setBookmarked(bookmarked ? 0 : 1)
    }
  }

  return {
    onBookmark: handleBookmark,
    loading,
    bookmarked,
  }
}

type useApiRequestParams = {
  path: string
  params?: Record<any, any>
}
export function useApiGetRequest({ path, params }: useApiRequestParams) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [data, setData] = useState<Record<string, any> | undefined>(undefined)

  async function fetch() {
    setLoading(true)
    setData(undefined)
    setError(undefined)
    const { error, result } = await HttpClient({ path, params })
    setLoading(false)

    if (error) {
      setError(error.message)
      return
    }

    if (result) {
      setData(result.data)
    }
  }

  return {
    fetch,
    loading,
    error,
    data,
  }
}

export function useFollowComic(id: number, _following: boolean) {
  const [following, setFollowing] = useState(_following)
  const { data, error, loading, fetch } = useApiGetRequest({
    path: ApiUtils.Endpoints.activities.follow.mirror,
    params: {
      cid: id,
    },
  })

  useEffect(() => {
    if (!error && !data) return
    if (error) {
      toast.error('Failed to follow comic')
      return
    }
    setFollowing((following) => !following)
    toast.success(data?.message)
  }, [error, loading, data])

  return {
    followComic: fetch,
    isLoading: loading,
    following,
  }
}

// export function useUser() {}

export function usePostComment(epid: number) {
  const [value, setValue] = useState('')
  const [loading, setLoading] = useState(false)
  // const [comment, setComment] = useState<CommentType[]>([])

  async function postComment() {
    if (!value || value.length > 200) return
    setLoading(true)
    const { result, error } = await HttpClient({
      path: ApiUtils.Endpoints.comment_actions.post({
        episodeId: epid.toString(),
      }).mirror,
      body: { comment: value },
      method: 'post',
    })
    setLoading(false)
    if (error) {
      toast.error('Failed to post comment!')
      return
    }

    if (result) {
      toast.success('Your comment has been posted')
    }
  }

  function handleChange(e: ChangeEvent<HTMLTextAreaElement>) {
    setValue(e.target.value)
  }

  return {
    value,
    loading,
    onChange: handleChange,
    onPostComment: postComment,
  }
}

export function useUserLocation() {
  const [country, setCountry] = useState('')

  async function getUserCountry() {
    const { data } = await axios.get('https://geolocation-db.com/json/')
    setCountry(data.country_name)
  }

  useEffect(() => {
    getUserCountry()
  }, [])

  return { country }
}

export function useIntercomWindow() {
  const { data, loading, fetch } = useApiGetRequest({
    path: ApiUtils.Endpoints.account.index.mirror,
  })

  function initializeIntercom(user?: UserProfile) {
    loadIntercom({
      appId: 'qz0v4zfq',
      name: user?.name || '',
      email: user?.email || '',
      ssr: false,
      initWindow: true,
      delay: 0,
    })
  }

  useEffect(() => {
    fetch()
  }, [])

  useEffect(() => {
    if (!loading) {
      initializeIntercom(data?.user)
    }
  }, [loading, data])
}
